import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, ArrowRight } from 'lucide-react';
import Container from '../components/common/Container';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { createInquiry } from '../redux/slice/inquiry.slice';
import { getAllProjects } from '../redux/slice/project.slice';
import { Toaster } from 'react-hot-toast';
import { useLocation } from 'react-router-dom';

const ContactItem = ({ icon: Icon, title, content, delay }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay }}
        className="group flex items-start gap-4 md:gap-6 p-0 md:p-6 rounded-2xl hover:bg-transparent md:hover:bg-white transition-all duration-300 border border-transparent md:hover:border-primary/10 md:hover:shadow-lg"
    >
        <div className="p-3 md:p-4 bg-secondary/10 rounded-full text-secondary group-hover:bg-secondary group-hover:text-white transition-colors duration-300 flex-shrink-0">
            <Icon size={20} className="md:w-6 md:h-6" />
        </div>
        <div>
            <h3 className="font-serif text-lg md:text-xl font-bold text-primary mb-1 md:mb-2">{title}</h3>
            <p className="text-secondary font-sans font-medium leading-relaxed whitespace-pre-line group-hover:text-primary transition-colors text-base">{content}</p>
        </div>
    </motion.div>
);

const InputField = ({ label, type = "text", placeholder, name, value, onChange, onBlur, error, touched }) => (
    <div className="group relative j_input_style">
        <input
            type={type}
            name={name}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            placeholder=" "
            className={`peer w-full bg-transparent border-b py-4 text-primary focus:outline-none focus:border-secondary transition-colors font-serif placeholder-transparent ${touched && error ? 'border-red-500' : 'border-primary/20'}`}
        />
        <label className={`absolute left-0 text-secondary text-sm transition-all duration-300 pointer-events-none
            peer-placeholder-shown:text-base peer-placeholder-shown:top-4
            peer-focus:-top-2 peer-focus:text-xs peer-focus:text-secondary peer-focus:uppercase peer-focus:tracking-widest
            ${value ? '-top-2 text-xs text-secondary uppercase tracking-widest' : ''}
            ${touched && error ? 'text-red-500' : ''}`}
        >
            {label}
        </label>
        {touched && error && (
            <div className="text-red-500 text-xs mt-1 absolute -bottom-5 right-0">{error}</div>
        )}
    </div>
);

const ContactPage = () => {
    const dispatch = useDispatch();
    const { loading } = useSelector((state) => state.inquiry);
    const { projects } = useSelector((state) => state.project);
    const location = useLocation();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    useEffect(() => {
        if (projects.length === 0) {
            dispatch(getAllProjects());
        }
    }, [dispatch]);

    const validationSchema = Yup.object({
        firstName: Yup.string().required('First Name is required'),
        lastName: Yup.string().required('Last Name is required'),
        email: Yup.string().email('Invalid email address').required('Email is required'),
        phone: Yup.string().matches(/^\d{10}$/, 'Phone number must be exactly 10 digits').required('Phone Number is required'),
        message: Yup.string().required('Message is required'),
        project: Yup.string().nullable()
    });

    const formik = useFormik({
        initialValues: {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            message: '',
            project: location.state?.selectedProject || ''
        },
        validationSchema,
        onSubmit: (values, { resetForm }) => {
            dispatch(createInquiry({
                firstName: values.firstName,
                lastName: values.lastName,
                email: values.email,
                phone: values.phone,
                message: values.message,
                subject: 'Contact Form Submission',
                project: values.project || null
            })).then((res) => {
                if (res.meta.requestStatus === 'fulfilled') {
                    resetForm();
                }
            });
        }
    });

    return (
        <div className="min-h-screen bg-background relative overflow-hidden selection:bg-secondary selection:text-white">
            {/* Animated Background Elements */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] bg-secondary/10 blur-[120px] rounded-full mix-blend-screen animate-pulse-slow" />
                <div className="absolute bottom-[-10%] left-[-5%] w-[500px] h-[500px] bg-blue-900/10 blur-[100px] rounded-full mix-blend-screen animate-pulse-slow" style={{ animationDelay: '1s' }} />
            </div>

            <Container className="font-sans relative z-10">
                <div className="pt-24 md:pt-32 pb-12 md:pb-20">
                    {/* Header Section */}
                    {/* <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="mb-12 md:mb-20 max-w-4xl"
                    >
                        <div className="flex items-center gap-4 mb-4 md:mb-6">
                            <span className="h-px w-8 md:w-12 bg-secondary"></span>
                            <span className="text-secondary text-xs md:text-sm font-bold tracking-[0.2em] uppercase">Get in Touch</span>
                        </div>
                        <h1 className="text-4xl md:text-7xl lg:text-8xl font-serif text-primary leading-tight">
                            Let's Discuss Your <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary to-primary italic pr-4">Future Home</span>
                        </h1>
                    </motion.div> */}


                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center max-w-4xl mx-auto px-4 mb-12 md:mb-20"
                    >
                        <div className="flex items-center justify-center gap-4 mb-6">
                            <span className="h-px w-8 md:w-12 bg-secondary/50"></span>
                            <span className="text-secondary text-xs md:text-sm font-bold tracking-[0.2em] uppercase">Get in Touch</span>
                            <span className="h-px w-8 md:w-12 bg-secondary/50"></span>
                        </div>
                        <h1 className="text-4xl md:text-7xl lg:text-8xl font-serif text-primary mb-6 md:mb-8 leading-tight font-bold">
                            Let's Discuss Your <br />
                            <span className="">Future Home</span>
                        </h1>
                        {/* <p className="text-secondary text-base md:text-lg leading-relaxed max-w-2xl mx-auto font-medium">
                            Whether you're interested in one of our premium properties or have a general inquiry, our team is here to assist you with exceptional service.
                        </p> */}
                    </motion.div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
                        {/* Contact Info */}
                        <div className="space-y-8 lg:sticky lg:top-32">
                            <p className="text-secondary text-base font-medium md:text-lg leading-relaxed mb-8 md:mb-12 max-w-md">
                                Whether you're interested in one of our premium properties or have a general inquiry, our team is here to assist you with exceptional service.
                            </p>

                            <div className="space-y-4">
                                <ContactItem
                                    icon={Phone}
                                    title="Phone"
                                    content={
                                        <>
                                            <a href="tel:+919979358000" className="hover:text-primary transition-colors">+91 99793 58000</a>
                                            <br />
                                            <a href="tel:+919979259000" className="hover:text-primary transition-colors">+91 99792 59000</a>
                                        </>
                                    }
                                    delay={0.2}
                                />
                                <ContactItem
                                    icon={Mail}
                                    title="Email"
                                    content={
                                        <a href="mailto:vallettagroup12@gmail.com" className="hover:text-primary transition-colors">
                                            vallettagroup12@gmail.com
                                        </a>
                                    }
                                    delay={0.3}
                                />
                                <ContactItem
                                    icon={MapPin}
                                    title="Office"
                                    content={
                                        <a href="https://maps.app.goo.gl/5VGqxK8hZNE1YWAw9" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
                                            VALLETTA VISTA, Palanpur, <br />
                                            Nr. Jalaram School, <br />
                                            Surat - 395009.
                                        </a>
                                    }
                                    delay={0.4}
                                />
                                <ContactItem
                                    icon={Clock}
                                    title="Office Hours"
                                    content={<>Mon - Sat: 10:00 AM - 7:00 PM<br />Sunday: By Appointment</>}
                                    delay={0.5}
                                />
                            </div>
                        </div>

                        {/* Contact Form */}
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            className="relative"
                        >
                            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent rounded-[2rem] blur-sm -z-10" />
                            <div className="bg-white backdrop-blur-xl p-6 md:p-12 rounded-[2rem] border border-primary/5 shadow-2xl">
                                <h3 className="text-3xl font-serif mb-2 text-primary">Send us a Message</h3>
                                <p className="text-secondary mb-10 text-sm">Fill out the form below and we'll get back to you shortly.</p>

                                <form onSubmit={formik.handleSubmit} className="space-y-8">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        <InputField
                                            label="First Name"
                                            name="firstName"
                                            value={formik.values.firstName}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            error={formik.errors.firstName}
                                            touched={formik.touched.firstName}
                                        />
                                        <InputField
                                            label="Last Name"
                                            name="lastName"
                                            value={formik.values.lastName}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            error={formik.errors.lastName}
                                            touched={formik.touched.lastName}
                                        />
                                    </div>
                                    <InputField
                                        label="Email Address"
                                        type="email"
                                        name="email"
                                        value={formik.values.email}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.errors.email}
                                        touched={formik.touched.email}
                                    />
                                    <InputField
                                        label="Phone Number"
                                        type="tel"
                                        name="phone"
                                        maxLength={10}
                                        value={formik.values.phone}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.errors.phone}
                                        touched={formik.touched.phone}
                                    />

                                    <div className="group relative pt-4">
                                        <label className={`block text-xs uppercase tracking-widest transition-colors mb-4 ${formik.values.project ? 'text-secondary' : 'text-gray-500'
                                            }`}>
                                            Interested In (Optional)
                                        </label>
                                        <div className="relative">
                                            <button
                                                type="button"
                                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                                className={`w-full flex items-center justify-between bg-transparent border-b py-4 text-left focus:outline-none transition-colors font-serif ${isDropdownOpen ? 'border-secondary' : 'border-primary/20'}`}
                                            >
                                                <span className={formik.values.project ? 'text-primary' : 'text-secondary'}>
                                                    {formik.values.project
                                                        ? projects.find(p => p._id === formik.values.project)?.title || 'Select a Project'
                                                        : 'Select a Project'}
                                                </span>
                                                <ArrowRight className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${isDropdownOpen ? 'rotate-90 text-secondary' : ''}`} />
                                            </button>

                                            {/* Custom Dropdown Options */}
                                            {isDropdownOpen && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: 10 }}
                                                    className="absolute z-50 left-0 right-0 mt-2 bg-white backdrop-blur-xl border border-primary/10 rounded-xl shadow-xl overflow-hidden max-h-60 overflow-y-auto"
                                                >
                                                    <div
                                                        onClick={() => {
                                                            formik.setFieldValue('project', '');
                                                            setIsDropdownOpen(false);
                                                        }}
                                                        className="px-6 py-3 text-secondary hover:bg-primary/5 hover:text-primary cursor-pointer transition-colors font-serif text-sm border-b border-primary/5"
                                                    >
                                                        General Inquiry
                                                    </div>
                                                    {projects && projects.map((project) => (
                                                        <div
                                                            key={project._id}
                                                            onClick={() => {
                                                                formik.setFieldValue('project', project._id);
                                                                setIsDropdownOpen(false);
                                                            }}
                                                            className={`px-6 py-3 cursor-pointer transition-colors font-serif text-sm border-b border-primary/5 last:border-0 ${formik.values.project === project._id ? 'bg-secondary/20 text-secondary' : 'text-primary hover:bg-primary/5'}`}
                                                        >
                                                            {project.title}
                                                        </div>
                                                    ))}
                                                </motion.div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="group relative pt-2">
                                        <textarea
                                            name="message"
                                            rows="4"
                                            placeholder=" "
                                            value={formik.values.message}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            className={`peer w-full bg-transparent border-b py-3 text-primary focus:outline-none focus:border-secondary transition-colors font-serif resize-none ${formik.touched.message && formik.errors.message ? 'border-red-500' : 'border-primary/20'}`}
                                        />
                                        <label className={`absolute left-0 text-secondary text-sm transition-all duration-300 pointer-events-none
                                            peer-placeholder-shown:text-base peer-placeholder-shown:top-4
                                            peer-focus:-top-2 peer-focus:text-xs peer-focus:text-secondary peer-focus:uppercase peer-focus:tracking-widest
                                            ${formik.values.message ? '-top-2 text-xs text-secondary uppercase tracking-widest' : ''}
                                            ${formik.touched.message && formik.errors.message ? 'text-red-500' : ''}`}
                                        >
                                            How can we help you?
                                        </label>
                                        {formik.touched.message && formik.errors.message && (
                                            <div className="text-red-500 text-xs mt-1 absolute -bottom-5 right-0">{formik.errors.message}</div>
                                        )}
                                    </div>

                                    <div className="pt-6">
                                        {/* <MagneticButton className="w-full"> */}
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="w-full bg-primary text-white rounded-full py-4 px-8 uppercase tracking-widest text-sm font-bold hover:bg-secondary hover:text-white transition-all duration-300 shadow-lg hover:shadow-secondary/30 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                        >
                                            {loading ? (
                                                <span className="flex items-center gap-2">
                                                    <span className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin"></span>
                                                    Sending...
                                                </span>
                                            ) : 'Send Message'}
                                        </button>
                                        {/* </MagneticButton> */}
                                    </div>
                                </form>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </Container>
        </div >
    );
};

export default ContactPage;
